<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';

import AttachmentGallery from '#nora-chat-ui/components/open-conversations/messages/message-attachments/AttachmentGallery.vue';
import MessageAttachmentPreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/MessageAttachmentPreview.vue';

interface MessageAttachmentProps {
  attachments: MessageAttachmentDTO[];
  isMyMessage: boolean;
  conversationId: string;
  messageId: string;
}

const props = defineProps<MessageAttachmentProps>();

const { t } = useI18n();

const MAX_VISIBLE_ATTACHMENTS = 4;
const MAX_WIDTH = 350;

const hasHiddenAttachments = computed(() => props.attachments.length > MAX_VISIBLE_ATTACHMENTS);

const visibleAttachments = computed(() => {
  if (hasHiddenAttachments.value) {
    return props.attachments.slice(0, MAX_VISIBLE_ATTACHMENTS - 1);
  }

  return props.attachments;
});

const hiddenAttachments = computed(() => props.attachments.slice(visibleAttachments.value.length));

/**
 * Width divided by height of a lone image or video, so the bubble can follow its shape.
 */
const singleAspectRatio = computed(() => {
  const [attachment] = props.attachments;

  if (props.attachments.length !== 1 || !attachment?.width || !attachment.height || attachment.deletedAt) {
    return 1;
  }

  return attachment.width / attachment.height;
});

const gridWidth = computed(() => Math.min(MAX_WIDTH, MAX_WIDTH * singleAspectRatio.value));

/**
 * Whether the tile spans the full width: a pair stacked on top of each other, or the first of three.
 */
function isWideTile(index: number) {
  return props.attachments.length === 2 || (props.attachments.length === 3 && index === 0);
}

/**
 * Aspect ratio of the tile at the given position in the grid.
 */
function tileAspectRatio(index: number) {
  if (props.attachments.length === 1) {
    return `${singleAspectRatio.value}`;
  }

  return isWideTile(index) ? '6 / 4' : '4 / 3';
}

const selectedAttachmentId = ref<string>();
const navigableAttachments = computed(() => props.attachments.filter(attachment => !attachment.deletedAt));
const selectedIndex = computed(() => navigableAttachments.value.findIndex(attachment => attachment.id === selectedAttachmentId.value));
const selectedAttachment = computed(() => navigableAttachments.value[selectedIndex.value]);

/**
 * Selects the neighbouring attachment in the gallery.
 */
function navigate(direction: -1 | 1) {
  const attachment = navigableAttachments.value[selectedIndex.value + direction];

  if (attachment) {
    selectedAttachmentId.value = attachment.id;
  }
}

/**
 * Opens the gallery on the first hidden attachment that is not deleted.
 */
function openHiddenAttachments() {
  selectedAttachmentId.value = hiddenAttachments.value.find(attachment => !attachment.deletedAt)?.id;
}
</script>

<template>
  <div
    :class="attachments.length <= 2 ? 'grid-cols-1' : 'grid-cols-2'"
    class="grid gap-px max-w-full"
    :style="{ width: `${gridWidth}px` }"
  >
    <MessageAttachmentPreview
      v-for="(attachment, ix) in visibleAttachments"
      :key="attachment.id"
      :class="{ 'col-span-2': attachments.length === 3 && ix === 0 }"
      :style="{ aspectRatio: tileAspectRatio(ix) }"
      :attachment="attachment"
      :is-my-message="isMyMessage"
      :max-size="isWideTile(ix) || attachments.length === 1 ? MAX_WIDTH : MAX_WIDTH / 2"
      :fill-frame="true"
      :hide-meta-data="true"
      :conversation-id="props.conversationId"
      :message-id="props.messageId"
      @open="selectedAttachmentId = attachment.id"
    />
    <button
      v-if="hasHiddenAttachments"
      type="button"
      class="flex items-center justify-center bg-black/60 text-white text-2xl cursor-pointer"
      style="aspect-ratio: 4 / 3"
      :aria-label="t('attachments.moreAttachments', { count: hiddenAttachments.length })"
      @click="openHiddenAttachments"
    >
      +{{ hiddenAttachments.length }}
    </button>
    <AttachmentGallery
      v-if="selectedAttachment"
      :attachment="selectedAttachment"
      :conversation-id="conversationId"
      :message-id="messageId"
      :can-previous="selectedIndex > 0"
      :can-next="selectedIndex < navigableAttachments.length - 1"
      @previous="navigate(-1)"
      @next="navigate(1)"
      @close="selectedAttachmentId = undefined"
    />
  </div>
</template>
